import * as React from 'react';

import {StepsData} from '../../types';
import {DirectionValue, Mode, Animation, Action} from '../../const';

import {linear, quad, makeEaseInOut} from '../../utils/animation';
import {getElementsByIndexes, getIndexesArray, getStepsData, getMinStepsData} from '../../utils/common';

interface Props {
  children: React.ReactNode;
  centerSlide: number;
  visualSlidesCount: number;
  slideMargin: number;
  duration: number;
  sliderHeight: number;
  visualPartWidth: string;
  slideWidth: number;
  inMinSteps: boolean;
  isFlipping: boolean;
  isFlicktyDots: boolean;
}

interface State {
  renderingSlidesIndexes: Array<number>;
  allSlides: Array<React.ReactNode>;
  sliderItemWidth: number;
  sliderItemHeight: number;
  sliderWidth: number;
  sliderMargin: number;
  animationProgress: Animation;
  mode: Mode;
  actionTo: Action;
  centerElementIndex: number;
  centerSlide: number;
  slideMargin: number;
  isSetSizes: boolean;
  visualSlidesCount: number;
  isNewIndex: boolean;
  newCenterIndex: number;
  iterationCount: number;
  animationDuration: number;
  animationCancel: boolean;
}

const withSlider = (Component) => {
  class WithSlider extends React.PureComponent<Props, State> {

    private _refItem: HTMLLIElement;
    private _animationId: number;
    private _refSlider: HTMLUListElement;
    private _startCoords: number;

    static defaultProps = {
      centerSlide: 0,
      visualSlidesCount: 3,
      slideMargin: 10,
      duration: 400,
      sliderHeight: null,
      visualPartWidth: `100%`,
      slideWidth: null,
      inMinSteps: false,
      isFlipping: true,
      isFlicktyDots: true,
    }

    constructor(props: Props) {
      super(props);

      this._refItem = null;
      this._refSlider = null;

      this.state = {
        allSlides: React.Children.toArray(props.children),
        slideMargin: props.slideMargin,
        sliderItemWidth: props.slideWidth,
        visualSlidesCount: props.visualSlidesCount,
        sliderItemHeight: null,
        sliderWidth: null,
        sliderMargin: null,
        centerElementIndex: props.centerSlide,
        newCenterIndex: props.centerSlide,
        centerSlide: props.centerSlide,
        renderingSlidesIndexes: [0],
        actionTo: Action.UNDEFINED,
        animationProgress: Animation.WITHOUT,
        mode: Mode.IN_ORDER,
        isSetSizes: true,
        isNewIndex: false,
        iterationCount: null,
        animationDuration: null,
        animationCancel: false,
      };

      this._btnClickHandler = this._btnClickHandler.bind(this);
      this._radioClickHandler = this._radioClickHandler.bind(this);
      this._sliderMouseSwipesHandler = this._sliderMouseSwipesHandler.bind(this);
      this._touchEndHandler = this._touchEndHandler.bind(this);
      this._touchStartHandler = this._touchStartHandler.bind(this);
    }

    static getDerivedStateFromProps(nextProps: Props, prevState: State) {
      const isAnimating: boolean = prevState.animationProgress !== Animation.WITHOUT || prevState.isNewIndex;

      const isChangedSlideMargin: boolean = prevState.slideMargin !== nextProps.slideMargin;
      const isChangedSlideWidth: boolean = (nextProps.slideWidth !== null) && (nextProps.slideWidth !== prevState.sliderItemWidth);
      const isChangedVisualSlidesCount: boolean = prevState.visualSlidesCount !== nextProps.visualSlidesCount;
      const isChangedSizes: boolean = isChangedSlideMargin || isChangedSlideWidth || isChangedVisualSlidesCount;

      const isChangedCenterSlide: boolean = nextProps.centerSlide !== prevState.centerSlide && nextProps.centerSlide !== prevState.centerElementIndex;

      const stateForNewSizes = {
        slideMargin: nextProps.slideMargin,
        renderingSlidesIndexes: [0],
        sliderItemWidth: nextProps.slideWidth,
        visualSlidesCount: nextProps.visualSlidesCount,
        isSetSizes: true,
        animationCancel: isAnimating,
        animationProgress: Animation.WITHOUT,
        isNewIndex: false,
        iterationCount: null,
        centerElementIndex: prevState.newCenterIndex,
      };

      const stateForNewIndexWithinAnimation = {
        animationCancel: true,
        animationProgress: Animation.WITHOUT,
        isNewIndex: false,
        iterationCount: null,
        centerElementIndex: nextProps.centerSlide,
        newCenterIndex: nextProps.centerSlide,
        centerSlide: nextProps.centerSlide
      };

      switch (true) {
        case isChangedSizes && isChangedCenterSlide:
          return {
            ...stateForNewSizes,
            ...stateForNewIndexWithinAnimation,
          };
        case isChangedCenterSlide && isAnimating:
          return stateForNewIndexWithinAnimation;
        case isChangedCenterSlide:
          return {
            centerSlide: nextProps.centerSlide,
            newCenterIndex: nextProps.centerSlide,
            isNewIndex: true,
          };
        case isChangedSizes:
          return stateForNewSizes;
      }

      return null;
    }

    render() {
      const {renderingSlidesIndexes, sliderMargin, allSlides, isSetSizes, sliderWidth, sliderItemHeight, sliderItemWidth, slideMargin, centerElementIndex} = this.state;
      const renderingSlides: Array<React.ReactNode> = getElementsByIndexes(renderingSlidesIndexes, allSlides);

      const sliderListHeight = !isSetSizes && this.props.sliderHeight ? {height: `${this.props.sliderHeight}px`} : {};
      const sliderListHorizon = !isSetSizes ? {marginLeft: sliderMargin, width: sliderWidth} : {};
      const sliderListStyle = {...sliderListHeight, ...sliderListHorizon};

      const sliderWrapperMaxWidth = !isSetSizes ? {maxWidth: `${this.state.visualSlidesCount * (sliderItemWidth + this.state.slideMargin * 2)}px`} : {};
      const sliderWrapperWidth = !isSetSizes ? {width: this.props.visualPartWidth} : {width: `100%`};
      let sliderWrapperMinHeight = !isSetSizes ? {minHeight: `${sliderItemHeight}px`} : {minHeight: `1000px`};
      if (!isSetSizes && this.props.sliderHeight) {
        sliderWrapperMinHeight = {minHeight: `auto`};
      }
      const sliderWrapperHeight = {...sliderListHeight};
      const sliderWrapperStyle = {...sliderWrapperWidth, ...sliderWrapperMinHeight, ...sliderWrapperMaxWidth, ...sliderWrapperHeight};

      const slideItemWidth = this.props.slideWidth ? {width: `${this.props.slideWidth}px`} : {};

      return (
        <Component
          sliderWrapperStyle={sliderWrapperStyle}
          sliderListStyle={sliderListStyle}
          onSliderMouseSwipes={this._sliderMouseSwipesHandler}
          // onSliderSwipes={this._sliderSwipesHandler}
          onSliderTouchStart={this._touchStartHandler}
          onSliderTouchEnd={this._touchEndHandler}
          refSlider={(el: HTMLUListElement) => {
            this._refSlider = el;
          }}
          refItem={(el: HTMLLIElement) => {
            this._refItem = el;
          }}
          renderingSlides={renderingSlides}
          slideItemWidth={slideItemWidth}
          slideMargin={slideMargin}
          isDisabled={this.state.animationProgress !== Animation.WITHOUT || this.state.isNewIndex}
          onBtnClick={this._btnClickHandler}
          allSlides={Array(allSlides.length).fill(``)}
          centerElementIndex={centerElementIndex}
          onRadioClick={this._radioClickHandler}
          isFlipping={this.props.isFlipping}
          isFlicktyDots={this.props.isFlicktyDots}
        >
        </Component>
      );
    }

    componentDidMount() {
      this._setSizes();
    }

    componentDidUpdate() {
      if (this.state.isNewIndex && this.state.iterationCount === null) {
        this._setTargetSlide();
      }
      if (this.state.isSetSizes) {
        this._setSizes();
      }
      switch (this.state.animationProgress) {
        case Animation.START:
          return this._startAnimation();
        case Animation.FINISHED:
          switch (this.state.actionTo) {
            case Action.TO_RIGHT:
              this.setState((oldState) => {
                const {sliderItemWidth, sliderWidth, renderingSlidesIndexes} = oldState;
                const newSliderWidth: number = sliderWidth - (sliderItemWidth + this.state.slideMargin * 2);
                return {
                  sliderMargin: -(newSliderWidth / 2),
                  renderingSlidesIndexes: renderingSlidesIndexes.slice(0, renderingSlidesIndexes.length - 1),
                  sliderWidth: newSliderWidth,
                  animationProgress: Animation.WITHOUT,
                };
              }, () => {
                if (this.state.isNewIndex) {
                  this._setCounterValue();
                }
              });
              break;
            case Action.TO_LEFT:
              this.setState((oldState) => {
                const {sliderItemWidth, sliderWidth, renderingSlidesIndexes} = oldState;
                const newSliderWidth: number = sliderWidth - (sliderItemWidth + this.state.slideMargin * 2);
                return {
                  sliderMargin: -(newSliderWidth / 2),
                  renderingSlidesIndexes: renderingSlidesIndexes.slice(1),
                  sliderWidth: newSliderWidth,
                  animationProgress: Animation.WITHOUT,
                };
              }, () => {
                if (this.state.isNewIndex) {
                  this._setCounterValue();
                }
              });
              break;
          }
      }
      return null;
    }

    componentWillUnmount() {
      window.cancelAnimationFrame(this._animationId);
    }

    private _setSizes() {
      const slideItemWidth: number = this.props.slideWidth ? this.props.slideWidth : this._refItem.offsetWidth;
      const sliderWidth: number = (slideItemWidth + this.state.slideMargin * 2) * this.state.visualSlidesCount;
      const renderingSlidesIndexes = getIndexesArray(this.state.centerElementIndex, this.state.visualSlidesCount, this.state.allSlides.length);
      this.setState({
        sliderItemWidth: this._refItem.offsetWidth,
        sliderItemHeight: this._refItem.offsetHeight,
        sliderWidth,
        sliderMargin: -(sliderWidth / 2),
        renderingSlidesIndexes,
        isSetSizes: false,
      });
    }

    private _setTargetSlide() {
      const iterationData: StepsData = this.props.inMinSteps ? getMinStepsData(this.state.centerElementIndex, this.state.newCenterIndex, this.state.allSlides.length) : getStepsData(this.state.centerElementIndex, this.state.newCenterIndex, this.state.allSlides.length);

      this.setState({
        animationDuration: this.props.duration / iterationData.steps,
        iterationCount: iterationData.steps,
        actionTo: iterationData.direction,
        mode: iterationData.steps === 1 ? Mode.IN_ORDER : Mode.TARGET,
      });
      if (iterationData.direction === Action.TO_RIGHT) {
        return this._goNextSlide();
      }
      return this._goPrevSlide();
    }

    private _transitionToNextIndex(value: string): void {
      let nextIndex: number;
      switch (value) {
        case DirectionValue.PREV:
          nextIndex = this.state.centerElementIndex + 1;
          if (nextIndex === this.state.allSlides.length) {
            nextIndex = 0;
          }
          break;
        case DirectionValue.NEXT:
          nextIndex = this.state.centerElementIndex - 1;
          if (nextIndex === -1) {
            nextIndex = this.state.allSlides.length - 1;
          }
          break;
      }
      this.setState({
        mode: Mode.IN_ORDER,
        newCenterIndex: nextIndex,
        isNewIndex: true,
      });
    }

    private _setCounterValue() {
      if ((this.state.iterationCount - 1) !== 0) {
        this.setState({
          iterationCount: this.state.iterationCount - 1,
        });
        if (this.state.actionTo === Action.TO_RIGHT) {
          return this._goNextSlide();
        }
        return this._goPrevSlide();
      }
      if ((this.state.iterationCount - 1) === 0) {
        this.setState({
          iterationCount: null,
          isNewIndex: false,
        });
      }
      return null;
    }

    private _startAnimation(): void {
      const {sliderMargin, sliderItemWidth, mode, actionTo, slideMargin} = this.state;

      this.setState({
        animationProgress: Animation.IN_PROGRESS,
        animationCancel: false,
      });

      const startMargin: number = sliderMargin;
      let finishMargin: number;
      switch (actionTo) {
        case Action.TO_RIGHT:
          finishMargin = sliderMargin - sliderItemWidth - (slideMargin * 2);
          break;
        case Action.TO_LEFT:
          finishMargin = sliderMargin + sliderItemWidth + (slideMargin * 2);
          break;
      }
      const difference: number = finishMargin - startMargin;

      let timingFunction = makeEaseInOut(quad);
      if (mode === Mode.TARGET) {
        timingFunction = linear;
      }
      let animationDuration: number = this.props.duration;
      if (mode === Mode.TARGET) {
        animationDuration = this.state.animationDuration;
      }

      this._animate(
          timingFunction,
          (value: number) => {
            this.setState({
              sliderMargin: startMargin - difference * value,
            });
          },
          animationDuration,
          () => {
            this.setState({
              animationProgress: Animation.FINISHED,
            });
          }
      );
    }

    private _animate(timing: (value: number) => number, draw: (value: number) => void, duration: number, animateFinished: () => void) {
      const start = performance.now();

      const animateStart = (time: number): void => {
        let timeFraction = (time - start) / duration;
        if (timeFraction > 1) {
          timeFraction = 1;
        }
        const progress = timing(timeFraction);

        draw(progress);

        if (this.state.animationCancel) {
          this._setSizes();
          return;
        }

        if (timeFraction < 1) {
          this._animationId = requestAnimationFrame(animateStart);
        } else {
          animateFinished();
        }
      };

      this._animationId = requestAnimationFrame(animateStart);
    }

    private _radioClickHandler(value: number) {
      this.setState({
        isNewIndex: true,
        newCenterIndex: value,
      });
    }

    private _btnClickHandler(value: string): void {
      this._transitionToNextIndex(value);
    }

    private _goNextSlide() {
      if (this.state.animationProgress === Animation.WITHOUT) {
        const {sliderItemWidth, sliderWidth, renderingSlidesIndexes, sliderMargin, allSlides, centerElementIndex, slideMargin} = this.state;
        const newSliderWidth: number = sliderWidth + (sliderItemWidth + slideMargin * 2);
        const newSliderMargin: number = sliderMargin - (sliderItemWidth + slideMargin * 2);
        let newItemIndex: number = renderingSlidesIndexes[0] - 1;
        if (newItemIndex === -1) {
          newItemIndex = allSlides.length - 1;
        }
        const newSlidesIndexes: Array<number> = [newItemIndex, ...renderingSlidesIndexes];
        let newCenterItemIndex: number = centerElementIndex - 1;
        if (newCenterItemIndex === -1) {
          newCenterItemIndex = allSlides.length - 1;
        }
        this.setState({
          sliderMargin: newSliderMargin,
          centerElementIndex: newCenterItemIndex,
          renderingSlidesIndexes: newSlidesIndexes,
          sliderWidth: newSliderWidth,
          animationProgress: Animation.START,
          actionTo: Action.TO_RIGHT,
        });
      }
    }

    private _goPrevSlide() {
      if (this.state.animationProgress === Animation.WITHOUT) {
        const {sliderItemWidth, sliderWidth, renderingSlidesIndexes, allSlides, centerElementIndex} = this.state;
        const newSliderWidth: number = sliderWidth + (sliderItemWidth + this.state.slideMargin * 2);
        let newItemIndex: number = renderingSlidesIndexes[renderingSlidesIndexes.length - 1] + 1;
        if (newItemIndex === allSlides.length) {
          newItemIndex = 0;
        }
        const newSlidesIndexes: Array<number> = [...renderingSlidesIndexes, newItemIndex];
        let newCenterItemIndex: number = centerElementIndex + 1;
        if (newCenterItemIndex === allSlides.length) {
          newCenterItemIndex = 0;
        }
        this.setState({
          centerElementIndex: newCenterItemIndex,
          renderingSlidesIndexes: newSlidesIndexes,
          sliderWidth: newSliderWidth,
          animationProgress: Animation.START,
          actionTo: Action.TO_LEFT,
        });
      }
    }

    private _sliderMouseSwipesHandler(evt: React.MouseEvent<HTMLUListElement, MouseEvent>) {
      if (this.state.animationProgress !== Animation.WITHOUT || this.state.isNewIndex) {
        return;
      }
      evt.preventDefault();

      const startCoords: number = evt.clientX;

      let dragged = false;

      const mouseMoveHandler = (moveEvt: MouseEvent) => {
        moveEvt.preventDefault();
        dragged = true;
        if (moveEvt.clientX > startCoords) {
          this._transitionToNextIndex(DirectionValue.NEXT);
        }
        if (moveEvt.clientX < startCoords) {
          this._transitionToNextIndex(DirectionValue.PREV);
        }
      };

      const mouseUpHandler = (upEvt: MouseEvent) => {
        upEvt.preventDefault();

        document.removeEventListener(`mousemove`, mouseMoveHandler);
        document.removeEventListener(`mouseup`, mouseUpHandler);

        if (dragged) {
          const targetElementClickHandler = (clickEvt: MouseEvent) => {
            clickEvt.preventDefault();
            this._refSlider.removeEventListener(`click`, targetElementClickHandler);
          };
          this._refSlider.addEventListener(`click`, targetElementClickHandler);
        }
      };
      document.addEventListener(`mousemove`, mouseMoveHandler);
      document.addEventListener(`mouseup`, mouseUpHandler);
    }

    private _touchStartHandler(evt: React.TouchEvent<HTMLUListElement>) {
      if (this.state.animationProgress !== Animation.WITHOUT || this.state.isNewIndex) {
        return;
      }
      this._startCoords = evt.changedTouches[0].screenX;
    }

    private _touchEndHandler(evt: React.TouchEvent<HTMLUListElement>) {
      if (this.state.animationProgress !== Animation.WITHOUT || this.state.isNewIndex) {
        return;
      }
      const endCoords: number = evt.changedTouches[0].screenX;
      if (this._startCoords > endCoords) {
        this._transitionToNextIndex(DirectionValue.PREV);
      }
      if (this._startCoords < endCoords) {
        this._transitionToNextIndex(DirectionValue.NEXT);
      }
    }
  }

  return WithSlider;
};

export default withSlider;

